import React, {
  useState, useEffect, useMemo, useCallback, useRef,
} from 'lib/react';
import { useLocation } from 'lib/router';
import {
  EVSnackbar, EVTypography, EVLinearProgress, EVButton, EVClear, EVGrid, EVAlert, EVIconButton, EVCheckCircleOutlineIcon,
} from 'lib/components';
import { useSelector, useDispatch } from 'lib/redux';
import { ERROR, NOTIFICATION_BAR_STATES } from 'constants/AppConstants';
import clsx from 'lib/clsx';
import useAbortController from 'hooks/useAbortController';
import { NOTIFICATION_CLOSE } from 'constants/ActionTypes';
import NotificationBarStyles from './NotificationBarStyles';

function NotificationBar() {
  const styles = NotificationBarStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const previousPathname = useRef(location.pathname);
  const {
    showNotification, status, title, footer,
  } = useSelector((state) => state.notificationBar);
  const [progress, setProgress] = useState(0);
  const errorState = useMemo(() => [NOTIFICATION_BAR_STATES.ERROR, NOTIFICATION_BAR_STATES.CLOSE].includes(status), [status]);
  const { resetController } = useAbortController();
  const [isFixed, setFixed] = useState(false);

  useEffect(() => {
    if (location.pathname !== previousPathname.current && status === NOTIFICATION_BAR_STATES.SUCCESS) {
      dispatch({ type: NOTIFICATION_CLOSE });
    }
  }, [location.pathname]);

  const handleProgress = useCallback(() => {
    setProgress((prevProgress) => (prevProgress >= 95 ? prevProgress : prevProgress + 5));
  }, []);

  const notificationObservedDivRef = useRef(null);

  useEffect(() => {
    const options = {
      root: document,
      rootMargin: '-10px',
      threshold: 1,
    };

    // Observe the notificationObservedDiv and set the snackbar to fixed position when it is not in the viewport
    const observer = new IntersectionObserver((entries) => {
      setFixed(!entries[0].isIntersecting);
    }, options);

    observer.observe(notificationObservedDivRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (status === NOTIFICATION_BAR_STATES.PROGRESS) {
      const timer = setInterval(handleProgress, 1000);
      return () => clearInterval(timer);
    }
    return (() => setProgress(0));
  }, [status, handleProgress]);

  const handleNotificationClose = () => {
    dispatch({ type: NOTIFICATION_CLOSE });
    resetController();
  };

  const errorAlert = (
    <EVAlert
      style={{ flexGrow: 1, borderBottom: 'unset' }}
      severity={ERROR}
      classes={{ icon: styles.errorAlertIcon, message: styles.alertMsg, root: styles.errorAlert }}
      action={(
        <EVIconButton
          className={styles.alertCloseButton}
          aria-label="close"
          size="small"
          onClick={handleNotificationClose}
        >
          <EVClear />
        </EVIconButton>
    )}
    >
      {title}
    </EVAlert>
  );

  const progressAlert = (
    <>
      <EVGrid style={{ flexGrow: 1 }}>
        <EVTypography className={styles.notificationBarHeader}>
          {status === NOTIFICATION_BAR_STATES.SUCCESS && (
          <EVCheckCircleOutlineIcon
            className={styles.notificationSuccessIcon}
          />
          )}
          {title}
        </EVTypography>
        {status === NOTIFICATION_BAR_STATES.PROGRESS && (
        <EVLinearProgress
          classes={{
            root: styles.root,
            bar: styles.bar,
          }}
          className={styles.progressBar}
          variant="determinate"
          color="secondary"
          value={progress}
        />
        )}

        <EVTypography variant="body2" className={styles.notificationBarFooter}>
          {footer}
        </EVTypography>

      </EVGrid>
      <EVGrid>
        <EVButton
          className={styles.closeButton}
          onClick={handleNotificationClose}
          id="notification-close-btn"
        >
          <EVClear style={{ color: 'white' }} />
        </EVButton>
      </EVGrid>
    </>
  );

  return (
    <>
      <div ref={notificationObservedDivRef} className={styles.notificationObservedDiv} />
      <EVSnackbar
        open={showNotification}
        className={clsx(styles.notificationContainer, errorState && styles.errorContainer, isFixed && 'fixed')}
      >
        {
          errorState
            ? errorAlert
            : progressAlert
        }
      </EVSnackbar>
    </>
  );
}

export default NotificationBar;
